import React, { useMemo } from 'react';
import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import TuneIcon from '@mui/icons-material/Tune';

/**
 * فیلتر مشترک صفحات گزارش و نمایش اطلاعات
 * به theme/rtlFormStyles/global styles دست نمی‌زند.
 */
const ReportFilterField = ({ label, value, onChange, options = [] }) => {
  const items = useMemo(
    () => [
      { value: '', label: 'همه' },
      ...options.map((name) => ({ value: name, label: name })),
    ],
    [options]
  );

  const selected = items.find((item) => item.value === (value || '')) || items[0];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        direction: 'rtl',
        width: '100%',
        minWidth: 0,
      }}
    >
      <Typography
        component="span"
        variant="body2"
        sx={{
          fontWeight: 700,
          color: value ? '#0073ea' : '#676879',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Autocomplete
        size="small"
        sx={{
          flex: 1,
          minWidth: 0,
          '& .MuiAutocomplete-endAdornment': {
            left: 9,
            right: 'auto',
          },
        }}
        options={items}
        value={selected}
        onChange={(_, option) => onChange(option?.value ?? '')}
        getOptionLabel={(option) => option?.label || 'همه'}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        noOptionsText="موردی یافت نشد"
        clearText="پاک کردن"
        popupIcon={<TuneIcon sx={{ fontSize: 17, color: '#00a86a' }} />}
        slotProps={{
          popper: {
            sx: { zIndex: 1600 },
            placement: 'bottom-end',
          },
          paper: {
            sx: {
              direction: 'rtl',
              mt: 0.5,
              borderRadius: 2,
              border: '1px solid #e6e9ef',
              boxShadow: '0 12px 32px rgba(50, 51, 56, 0.12)',
            },
          },
          clearIndicator: {
            sx: { color: '#c5c7d0' },
          },
        }}
        renderOption={(props, option, { selected: isSelected }) => {
          const { key, ...rest } = props;
          return (
            <Box
              component="li"
              key={key}
              {...rest}
              sx={{
                display: 'flex !important',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                py: 1,
                px: 1.5,
                direction: 'rtl',
                fontWeight: isSelected ? 700 : 500,
                bgcolor: isSelected ? '#e5f4ff' : 'transparent',
                color: isSelected ? '#0073ea' : '#323338',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
                {option.label}
              </Typography>
              {isSelected && <CheckIcon sx={{ fontSize: 16, color: '#0073ea' }} />}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="انتخاب کنید..."
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#fff',
                direction: 'rtl',
                '& fieldset': {
                  borderColor: value ? '#cce5ff' : '#e6e9ef',
                },
                '&:hover fieldset': {
                  borderColor: '#00c875',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#00a86a',
                },
              },
              '& .MuiAutocomplete-input': {
                textAlign: 'right',
                fontWeight: value ? 700 : 500,
                color: value ? '#323338' : '#676879',
              },
            }}
          />
        )}
      />
    </Box>
  );
};

export default ReportFilterField;
